///
/// EarthBrowser
///
/// Created by Matt Giger on 9/9/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Button } from "@/components/ui/button"
import type { LinkedAccountRecord } from "@shared-types/user"
import { useGetUserAccountInfoQuery } from "@/redux/userApi"
import { LinkIcon, Loader2, MonitorSmartphoneIcon } from "lucide-react"
import { useState } from "react"
import { DevicesTable } from "./DevicesTable"
import { LinkedAccountTable } from "./LinkedAccountTable"
import { PasskeysSection } from "./PasskeysSection"
import { ProviderLinkButtons } from "./ProviderLinkButtons"
import { SectionHeader } from "./SectionHeader"

export function SecurityTab() {
	const { data: accountInfo, isLoading: isLoadingAccountInfo } = useGetUserAccountInfoQuery()
	const [showLinkOptions, setShowLinkOptions] = useState(false)

	// Need at least one remaining auth method to allow unlinking
	const hasMultipleAccounts = (accountInfo?.accounts?.length ?? 0) > 1
	const passkeyCount = accountInfo?.passkeys?.length ?? 0
	const canUnlinkAccount = hasMultipleAccounts || passkeyCount > 0

	const linkedProviders = new Set(accountInfo?.accounts?.map((acc: LinkedAccountRecord) => acc.provider) || [])
	const accounts = accountInfo?.accounts ?? []
	const devices = accountInfo?.devices ?? []

	return (
		<div className="space-y-4">
			{/* Linked Accounts */}
			<div data-section="linked-accounts" className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
				<SectionHeader
					icon={LinkIcon}
					title="Linked Accounts"
					description={accounts.length > 0
						? `${accounts.length} account${accounts.length > 1 ? 's' : ''} connected for seamless sign-in`
						: 'Connect Apple, Google, Microsoft, or GitHub for faster sign-in'}
					color="cyan"
				/>

				{isLoadingAccountInfo ? (
					<div className="flex items-center justify-center py-4">
						<Loader2 className="h-5 w-5 animate-spin text-cyan-500" />
					</div>
				) : (
					<div className="space-y-3">
						{accounts.length > 0 && (
							<div className="rounded-lg border border-border overflow-hidden">
								<LinkedAccountTable data={accounts} canUnlink={canUnlinkAccount} />
							</div>
						)}
						{showLinkOptions ? (
							<ProviderLinkButtons
								linkedProviders={linkedProviders}
								onClose={() => setShowLinkOptions(false)}
							/>
						) : (
							<Button
								onClick={() => setShowLinkOptions(true)}
								variant="outline"
								className="w-full rounded-lg border-cyan-500/50 hover:bg-cyan-500/10 hover:border-cyan-500"
							>
								<LinkIcon className="mr-2 h-4 w-4" />
								Link an Account
							</Button>
						)}
					</div>
				)}
			</div>

			{/* Passkeys */}
			<div className="rounded-xl border border-border bg-muted/30 p-4">
				<PasskeysSection />
			</div>

			{/* Devices */}
			<div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
				<SectionHeader
					icon={MonitorSmartphoneIcon}
					title="Devices"
					description={devices.length > 0
						? `${devices.length} trusted device${devices.length > 1 ? 's' : ''} with access to your account`
						: 'Your trusted devices will appear here once you sign in from them'}
					color="cyan"
				/>

				{isLoadingAccountInfo ? (
					<div className="flex items-center justify-center py-4">
						<Loader2 className="h-5 w-5 animate-spin text-cyan-500" />
					</div>
				) : devices.length === 0 ? (
					<div className="rounded-lg border border-dashed border-border p-4 flex items-center justify-center">
						<p className="text-sm text-muted-foreground italic">No devices yet</p>
					</div>
				) : (
					<div className="rounded-lg border border-border overflow-hidden">
						<DevicesTable data={devices} />
					</div>
				)}
			</div>
		</div>
	)
}
